import { AsyncPipe, NgClass, TitleCasePipe } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { ChartType } from 'app/models/charts.model';
import { RoundToKPipe } from 'app/modules/dashboard/pipes/round-to-k.pipe';
import { TimerPipe } from 'app/modules/dashboard/pipes/timer.pipe';
import { Chart, ChartData, ChartOptions, Plugin } from 'chart.js';
import { BehaviorSubject, Subscription } from 'rxjs';

import { ChartHoverService } from '../../services/chart-hover.service';
import { FloorPipe } from 'app/pipe/floor.pipe';

export interface TrainingSection {
  average_power: null | number;
  distance: number;
  elevation: number;
  polyline: string;
  position: number;
  slope: number;
  workout_load: null | number;
}

@Component({
  imports: [NgClass, AsyncPipe, TitleCasePipe, FloorPipe],
  selector: 'tl-ts-chart-component',
  standalone: true,
  styleUrls: ['./tl-ts-chart.component.scss'],
  template: `
    <div [ngClass]="classes">
      <div id="chart-header">
        <div class="title">{{ title | titlecase }}</div>
        <div class="data">
          {{ progress | async | floor }}
          {{ unit === 'metric' ? ('m' | titlecase) : ('feets' | titlecase) }}
        </div>
      </div>
      <div id="chart-container">
        <canvas id="TlTsChartRef">{{ chart }}</canvas>
      </div>
    </div>
  `,
})
export class TrainingLogTrainingSectionChartComponent implements OnInit {
  @Input() size: 'large' | 'medium' | 'small' = 'medium';
  @Input() unit: 'imperial' | 'metric';

  @Input() tension: number = 0.2;
  @Input() title!: string;
  @Input() trainingsSections!: TrainingSection[];
  chart: any;
  data: number[] = [];

  getGradientBackground = (context: any, color: any) => {
    const {
      chartArea: { bottom, top },
      ctx,
    } = context.chart;
    const gradientBg = ctx.createLinearGradient(0, top, 0, bottom);
    gradientBg.addColorStop(0, color + '80'); // 50% opacity
    gradientBg.addColorStop(0.5, color + '33'); // 20% opacity
    gradientBg.addColorStop(1, color + '00'); // 0% opacity
    return gradientBg;
  };
  labels: number[] = [];
  progress: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  roundToKPipe: RoundToKPipe = new RoundToKPipe();

  subscriptions: Subscription[] = [];

  timerPipe: TimerPipe = new TimerPipe();

  constructor(private chartHoverService: ChartHoverService) { } // Inject the service

  getSectionColorBySlope(slopePoint: number) {
    const sectionColor: string =
      slopePoint > 8
        ? '#D25E58'
        : slopePoint > 5
          ? '#FDA250'
          : slopePoint > 2
            ? '#ffc947'
            : slopePoint > -2
              ? '#50AD5A'
              : '#428ff3';
    return sectionColor;
  }

  public get classes(): string[] {
    return [`chart-${this.size}`];
  }

  ngOnInit(): void {
    // console.log(this.trainingsSections);
    this.labels = this.trainingsSections.map(
      (trainingSection: TrainingSection) => {
        const lb: number = Math.round(trainingSection.position);
        return lb > 0 ? lb - 1 : lb; // -1 Pour que le graphique commence à 0 et pas à 1.
      }
    );
    this.data = this.trainingsSections.map(
      (trainingSection: TrainingSection) => {
        if (this.unit === 'metric') return trainingSection.elevation;
        return trainingSection.elevation * 3.2809;
      }
    );

    const yMaxValue: number =
      Math.max(...this.data) > 100
        ? Math.round(Math.max(...this.data) / 100) * 100 + 100
        : Math.max(...this.data) > 50
          ? Math.round(Math.max(...this.data) / 10) * 10 + 10
          : Math.max(...this.data) + 1;

    let chartdata: ChartData<any> = {
      datasets: [{ data: this.data }],
      labels: this.labels,
    };

    // The initial options.
    let options: ChartOptions<any> = {
      defaultFontFamily: (Chart.defaults.font.family = "'Sk-Modernist-Bold'"),
      defaultFontSize: (Chart.defaults.font.size = 14),

      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          bodyColor: '#c8c8c8',
          caretSize: 6,
          xAlign: 'center',
          yAlign: 'bottom',
        },
      },
      scales: {
        x: {
          border: {
            color: '#f4f4f4',
            width: 3,
          },
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: 0,
          },
        },
        y: {
          border: {
            display: false,
          },
          grid: {
            tickWidth: 0,
          },
          ticks: {
            crossAlign: 'center',
            padding: 8,
          },
        },
      },
    };

    /**
     * ? ----------- PLUGIN
     * Permet d'afficher la ligne ou se trouve le curseur.
     * Il et ajouté au plugins a la creation des graphiques,
     * en meme temps que les options.
     */
    const drawVerticalLine: Plugin = {
      beforeDraw: (data: any, args: any, plugins: any) => {
        const ctx = data.ctx;
        if (data._active && data._active.length > 0) {
          ctx.save();
          const activePoint = data.tooltip._active[0];
          ctx.beginPath();
          ctx.moveTo(activePoint.element.x, data.chartArea.top);
          ctx.lineTo(activePoint.element.x, activePoint.element.y);
          ctx.lineWidth = 2;
          ctx.strokeStyle = '#c8c8c8';
          ctx.stroke();

          // Bottom line
          ctx.moveTo(activePoint.element.x, activePoint.element.y);
          ctx.lineTo(activePoint.element.x, data.chartArea.bottom);
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.restore();
        }
      },
      id: 'drawVerticalLine',
    };
    const plugins: any = [drawVerticalLine];

    chartdata = {
      ...chartdata,
      datasets: [
        {
          ...chartdata.datasets[0],
          fill: true,
          segment: {
            backgroundColor: (ctx: any) => {
              const segment0Index = ctx.p0DataIndex;
              const slope0: number =
                this.trainingsSections[segment0Index].slope;
              return this.getGradientBackground(
                ctx,
                this.getSectionColorBySlope(slope0)
              );
            },
            borderColor: (ctx: any) => {
              const segment0Index = ctx.p0DataIndex;
              const slope0: number =
                this.trainingsSections[segment0Index].slope;
              return this.getSectionColorBySlope(slope0);
            },
          },
          tension: this.tension,
        },
      ],
    };

    options = {
      ...options,
      elements: {
        point: {
          radius: 0,
        },
      },
      interaction: {
        intersect: false,
      },
      plugins: {
        ...options.plugins,
        tooltip: {
          ...options.plugins.tooltip,
          callbacks: {
            label: (data: any) => {
              this.progress.next(data.raw);
              return this.timerPipe.transform(data.label);
            },
            title: () => {
              return '';
            },
          },
          displayColors: false,
          mode: 'index',
        },
      },
      scales: {
        x: {
          ...options.scales.x,
          ticks: {
            ...options.scales.x.ticks,
            align: 'end',
            autoSkipPadding: 30,
            callback: (data: any) => {
              return data > 0
                ? this.timerPipe.transform(this.labels[data])
                : '';
            },
            sampleSize: 20,
          },
        },
        y: {
          max: yMaxValue,
          stack: 'stack',
          ...options.scales.y,
          ticks: {
            ...options.scales.y.ticks,
            callback: (data: any) => {
              return `${this.roundToKPipe.transform(data)}`;
            },
          },
        },
      },
    };

    this.chart = new Chart('TlTsChartRef', {
      data: chartdata,
      options: options,
      plugins: plugins,
      type: ChartType.LINE,
    });

    this.chart.options.onHover = (event: any, elements: any[]) => {
      if (elements.length) {
        const element = elements[0];
        const label: number = this.chart.data.labels[element.index];
        this.chartHoverService.setHoverLabel(label); // Emit hover label
      } else {
        this.chartHoverService.setHoverLabel(null);
      }
    };

    // Subscribe to hover label changes
    this.subscriptions.push(
      this.chartHoverService.hoverLabel$.subscribe((label) => {
        if (label && this.chart) {
          const index = this.chart.data.labels.indexOf(label);
          const meta = this.chart.getDatasetMeta(0);
          const points = meta.data;
          const activePoint = points[index];

          if (activePoint) {
            this.chart.tooltip.setActiveElements(
              [
                {
                  datasetIndex: 0,
                  index: index,
                },
              ],
              { x: activePoint.x, y: activePoint.y }
            );
            this.chart.update('none'); // Use 'none' to avoid animation
            this.chart.render(); // Force render to trigger plugins
          }
        } else {
          this.chart.tooltip.setActiveElements([], { x: 0, y: 0 });
          this.chart.update('none');
          this.chart.render();
        }
      })
    );
  }
}
